import React, { useEffect, useState, useMemo } from "react";
import { cardCls, btnPrimary } from "../ui/styleTokens";
import axiosInstance from "../../utils/api";
import { MdCheckCircle } from "react-icons/md";
import { FiCalendar, FiShield, FiTag } from "react-icons/fi";

const FEATURE_META = {
  retroplanning: { icon: FiCalendar, bg: "bg-blue-50", text: "text-blue-600" },
  cleaning: { icon: MdCheckCircle, bg: "bg-emerald-50", text: "text-emerald-600" },
  hse: { icon: FiShield, bg: "bg-amber-50", text: "text-amber-600" },
  badging: { icon: FiTag, bg: "bg-purple-50", text: "text-purple-600" },
};
const DEFAULT_META = { icon: FiTag, bg: "bg-gray-100", text: "text-gray-500" };

const SkeletonCard = () => (
  <div className="border border-gray-200 rounded-xl p-4 animate-pulse space-y-3">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-gray-100" />
      <div className="h-3.5 w-24 bg-gray-200 rounded" />
    </div>
    <div className="h-14 bg-gray-100 rounded-lg" />
    <div className="h-8 w-24 bg-gray-100 rounded-lg ml-auto" />
  </div>
);

const PricingTab = () => {
  const [features, setFeatures] = useState([]);
  const [original, setOriginal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    axiosInstance.get("/billing/pricing")
      .then((r) => {
        setFeatures(r.data || []);
        setOriginal(r.data || []);
      })
      .catch((err) => console.error("Failed to load pricing", err))
      .finally(() => setLoading(false));
  }, []);

  const updateField = (i, field, value) => {
    const updated = [...features];
    updated[i] = { ...updated[i], [field]: value };
    setFeatures(updated);
    setSavedAt(null);
  };

  const changedIds = useMemo(() => {
    const ids = new Set();
    features.forEach((f, i) => {
      const o = original[i];
      if (o && (o.description !== f.description || o.price !== f.price)) {
        ids.add(f._id);
      }
    });
    return ids;
  }, [features, original]);

  const isDirty = changedIds.size > 0;
  const totalIfAllEnabled = useMemo(
    () => features.reduce((sum, f) => sum + (Number(f.price) || 0), 0),
    [features]
  );

  const handleDiscard = () => setFeatures(original);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance.put("/billing/pricing", { features });
      setFeatures(res.data);
      setOriginal(res.data);
      setSavedAt(Date.now());
    } catch (err) {
      console.error("Failed to save pricing", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`${cardCls} p-5 pb-0 relative`}>
      <div className="flex items-start justify-between mb-5">
        <div>
          <h4 className="font-semibold text-gray-900 text-sm">Feature pricing</h4>
          <p className="text-sm text-gray-500 mt-1">
            Set what each module costs. Admins see these prices when choosing what to enable.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-4">
          {savedAt && !isDirty && (
            <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
              <MdCheckCircle size={14} /> Saved
            </span>
          )}
          {!loading && features.length > 0 && (
            <div className="text-right">
              <p className="text-sm font-semibold text-gray-900 tabular-nums">{totalIfAllEnabled.toFixed(0)} MAD</p>
              <p className="text-xs text-gray-400">if all enabled</p>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-5">
        {loading ? (
          <>
            <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
          </>
        ) : features.length === 0 ? (
          <p className="text-sm text-gray-400 py-8 text-center sm:col-span-2">
            No features found. Run the seed script to create them.
          </p>
        ) : (
          features.map((f, i) => {
            const meta = FEATURE_META[f.key] || DEFAULT_META;
            const Icon = meta.icon;
            const changed = changedIds.has(f._id);
            return (
              <div
                key={f._id}
                className={`border rounded-xl p-4 transition-colors ${changed ? "border-blue-300 bg-blue-50/30" : "border-gray-200 bg-white"}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${meta.bg} ${meta.text}`}>
                      <Icon size={18} />
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{f.name}</p>
                  </div>
                  {changed && (
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" title="Unsaved change" />
                  )}
                </div>

                <textarea
                  value={f.description || ""}
                  onChange={(e) => updateField(i, "description", e.target.value)}
                  placeholder="Add a description"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition"
                />

                <div className="flex justify-end mt-3">
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      value={f.price}
                      onChange={(e) => updateField(i, "price", Number(e.target.value))}
                      className="w-28 pl-3 pr-12 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 text-right focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">MAD</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {isDirty && (
        <div className="sticky bottom-0 -mx-5 px-5 py-3 bg-white border-t border-gray-200 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {changedIds.size} feature{changedIds.size > 1 ? "s" : ""} changed
          </span>
          <div className="flex gap-2">
            <button onClick={handleDiscard} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition">
              Discard
            </button>
            <button onClick={handleSave} disabled={saving} className={btnPrimary}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
export default PricingTab;